import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { adminApi } from '@/lib/api'
import { apiThunk, type ThunkCtx } from '@/app/thunk'
import type { RootState } from '@/app/store'
import { dealerRole, statusTone, type StatusTone } from '@/lib/format'
import { toastPushed } from '@/features/ui/uiSlice'
import type {
  CreateSubDealerBody,
  Dealer,
  DealerRole,
  SubDealer,
  UpdateDealerBody,
  UpdateSubDealerBody
} from '@/types'

export type RoleFilter = 'all' | DealerRole
export type StatusFilter = 'all' | StatusTone

export interface DealersState {
  items: Dealer[]
  loading: boolean
  error: string | null
  search: string
  role: RoleFilter
  status: StatusFilter
  /** user_id of the dealer whose detail panel is open. */
  selectedUserId: string | null
  subDealers: SubDealer[]
  subDealersLoading: boolean
}

const initialState: DealersState = {
  items: [],
  loading: false,
  error: null,
  search: '',
  role: 'all',
  status: 'all',
  selectedUserId: null,
  subDealers: [],
  subDealersLoading: false
}

export const fetchDealers = apiThunk<Dealer[]>('dealers/fetch', async (_arg, config) => {
  const rows = await adminApi.listDealers(config)
  return rows ?? []
})

export const fetchSubDealers = apiThunk<SubDealer[], string>(
  'dealers/fetchSubs',
  async (userId, config) => {
    const payload = await adminApi.listSubDealers(config, userId)
    return payload?.data ?? []
  }
)

function isPrimary(state: RootState, userId: string): boolean {
  const d = state.dealers.items.find((x) => x.user_id === userId)
  return !!d && dealerRole(d) === 'primary'
}

/** After any mutation: reload the list, and the open panel's sub-dealers too. */
async function refresh(ctx: ThunkCtx): Promise<void> {
  await ctx.dispatch(fetchDealers())
  const selected = ctx.getState().dealers.selectedUserId
  if (selected && isPrimary(ctx.getState(), selected)) {
    await ctx.dispatch(fetchSubDealers(selected))
  }
}

export const saveDealer = apiThunk<string, { userId: string; body: UpdateDealerBody }>(
  'dealers/save',
  async ({ userId, body }, config, ctx) => {
    await adminApi.updateDealer(config, userId, body)
    ctx.dispatch(toastPushed('Dealer updated'))
    await refresh(ctx)
    return userId
  }
)

export const setDealerActive = apiThunk<string, { userId: string; active: boolean }>(
  'dealers/setActive',
  async ({ userId, active }, config, ctx) => {
    await adminApi.updateDealer(config, userId, { is_active: active })
    ctx.dispatch(toastPushed(active ? 'Dealer activated' : 'Dealer deactivated'))
    await refresh(ctx)
    return userId
  }
)

export const deleteDealer = apiThunk<string, string>(
  'dealers/delete',
  async (userId, config, ctx) => {
    await adminApi.deleteDealer(config, userId)
    ctx.dispatch(toastPushed('Deleted ' + userId))
    await ctx.dispatch(fetchDealers())
    return userId
  }
)

export const setTrial = apiThunk<string, { userId: string; days: number }>(
  'dealers/trial',
  async ({ userId, days }, config, ctx) => {
    await adminApi.setTrial(config, userId, days)
    ctx.dispatch(toastPushed('Trial set to ' + days + (days === 1 ? ' day' : ' days')))
    await refresh(ctx)
    return userId
  }
)

export const assignPlan = apiThunk<string, { userId: string; planId: number; planName: string }>(
  'dealers/assignPlan',
  async ({ userId, planId, planName }, config, ctx) => {
    await adminApi.assignPlan(config, userId, planId)
    ctx.dispatch(toastPushed('Assigned ' + planName))
    await refresh(ctx)
    return userId
  }
)

export const revokeSubscription = apiThunk<string, string>(
  'dealers/revoke',
  async (userId, config, ctx) => {
    await adminApi.revoke(config, userId)
    ctx.dispatch(toastPushed('Subscription revoked'))
    await refresh(ctx)
    return userId
  }
)

export const createSubDealer = apiThunk<void, CreateSubDealerBody>(
  'dealers/createSub',
  async (body, config, ctx) => {
    await adminApi.createSubDealer(config, body)
    ctx.dispatch(toastPushed('Sub-dealer ' + body.name + ' created'))
    await refresh(ctx)
  }
)

export const saveSubDealer = apiThunk<void, { userId: string; body: UpdateSubDealerBody }>(
  'dealers/saveSub',
  async ({ userId, body }, config, ctx) => {
    await adminApi.updateSubDealer(config, userId, body)
    ctx.dispatch(toastPushed('Sub-dealer updated'))
    await refresh(ctx)
  }
)

export const deleteSubDealer = apiThunk<void, string>(
  'dealers/deleteSub',
  async (userId, config, ctx) => {
    await adminApi.deleteSubDealer(config, userId)
    ctx.dispatch(toastPushed('Deleted ' + userId))
    await refresh(ctx)
  }
)

const dealersSlice = createSlice({
  name: 'dealers',
  initialState,
  reducers: {
    searchChanged(state, action: PayloadAction<string>) {
      state.search = action.payload
    },
    roleFilterChanged(state, action: PayloadAction<RoleFilter>) {
      state.role = action.payload
    },
    statusFilterChanged(state, action: PayloadAction<StatusFilter>) {
      state.status = action.payload
    },
    filtersCleared(state) {
      state.search = ''
      state.role = 'all'
      state.status = 'all'
    },
    dealerSelected(state, action: PayloadAction<string | null>) {
      state.selectedUserId = action.payload
      state.subDealers = []
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDealers.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchDealers.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.items = action.payload
      })
      .addCase(fetchDealers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Could not load dealers'
      })
      .addCase(fetchSubDealers.pending, (state) => {
        state.subDealersLoading = true
      })
      .addCase(fetchSubDealers.fulfilled, (state, action) => {
        state.subDealersLoading = false
        state.subDealers = action.payload
      })
      .addCase(fetchSubDealers.rejected, (state) => {
        state.subDealersLoading = false
        state.subDealers = []
      })
      .addCase(deleteDealer.fulfilled, (state, action) => {
        if (state.selectedUserId === action.payload) state.selectedUserId = null
      })
  }
})

export const {
  searchChanged,
  roleFilterChanged,
  statusFilterChanged,
  filtersCleared,
  dealerSelected
} = dealersSlice.actions

// ---- selectors ----
const selectAll = (s: RootState): Dealer[] => s.dealers.items
const selectSearch = (s: RootState): string => s.dealers.search
const selectRole = (s: RootState): RoleFilter => s.dealers.role
const selectStatus = (s: RootState): StatusFilter => s.dealers.status

export const selectVisibleDealers = createSelector(
  [selectAll, selectSearch, selectRole, selectStatus],
  (items, search, role, status) => {
    const q = search.trim().toLowerCase()
    return items.filter((d) => {
      if (role !== 'all' && dealerRole(d) !== role) return false
      if (status !== 'all' && statusTone(d.status) !== status) return false
      if (!q) return true
      return (d.email + ' ' + d.name + ' ' + d.user_id).toLowerCase().includes(q)
    })
  }
)

export const selectSelectedDealer = createSelector(
  [selectAll, (s: RootState) => s.dealers.selectedUserId],
  (items, id) => (id ? (items.find((d) => d.user_id === id) ?? null) : null)
)

/** Headline counts for the dashboard, derived from the list we already load. */
export const selectDealerStats = createSelector([selectAll], (items) => {
  const accounts = items.filter((d) => !d.is_admin)
  const primaries = accounts.filter((d) => dealerRole(d) === 'primary')
  const tally = (tone: StatusTone): number =>
    primaries.filter((d) => statusTone(d.status) === tone).length
  return {
    total: accounts.length,
    primaries: primaries.length,
    subs: accounts.length - primaries.length,
    paid: tally('paid'),
    trial: tally('trial'),
    pending: tally('pending'),
    expired: tally('expired'),
    inactive: accounts.filter((d) => !d.is_active).length,
    seats: accounts.reduce((sum, d) => sum + (d.allowed_clients || 0), 0)
  }
})

/**
 * Accounts newest first, for the dashboard's activity panel. The panel decides
 * how many rows to show; this just fixes the order.
 */
export const selectRecentDealers = createSelector([selectAll], (items) =>
  [...items]
    .filter((d) => !d.is_admin)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
)

export default dealersSlice.reducer
